import { Navigate, Outlet } from "react-router-dom";
import { useAuth } from "../context/AuthContext";
import { Snackbar, Alert, CircularProgress, Box } from "@mui/material";
import { useTranslation } from "react-i18next";
import { useEffect, useRef, useState } from "react";
import axios from "axios";

export const PrivateRoute = ({ allowedRoles }) => {
  const { auth, loading, logout } = useAuth();
  const { t } = useTranslation();
  const [notification, setNotification] = useState({
    open: false,
    message: "",
    severity: "info",
  });

  const handlingError = useRef(false);

  const showNotification = (message, severity = "info") => {
    setNotification({ open: true, message, severity });
  };

  const hideNotification = () => {
    setNotification((prev) => ({ ...prev, open: false }));
  };

  useEffect(() => {
    if (!loading && auth && !allowedRoles.includes(auth.role)) {
      showNotification(t("privateRoute.insufficientPermissions"), "warning");
      setTimeout(() => logout(), 3000);
    }
  }, [auth, loading, allowedRoles, t, logout]);

  useEffect(() => {
    const interceptor = axios.interceptors.response.use(
      (response) => response,
      (error) => {
        const status = error.response?.status;
        const message = error.response?.data?.error;
        const url = error.config?.url || "";


        if (url.includes("/api/user")) return Promise.reject(error);


        if (handlingError.current) return Promise.reject(error);


        const isAuthError = status === 401 || status === 403;

        if (isAuthError) {
          handlingError.current = true;
          showNotification(t("privateRoute.sessionExpired"), "info");
          setTimeout(() => {
            logout();
            handlingError.current = false;
          }, 2000);
        }

        return Promise.reject(error);
      }
    );

    return () => axios.interceptors.response.eject(interceptor);
  }, [t, logout]);

  if (loading) {
    return (
      <Box
        sx={{
          display: "flex",
          justifyContent: "center",
          alignItems: "center",
          height: "100vh",
          backgroundColor: "background.default",
        }}
      >
        <CircularProgress size={60} thickness={4} sx={{ color: "primary.main" }} />
      </Box>
    );
  }

  if (!auth || !allowedRoles.includes(auth.role)) {
    return <Navigate to="/" replace />;
  }

  return (
    <>
      <Outlet />
      <Snackbar
        open={notification.open}
        autoHideDuration={4000}
        onClose={hideNotification}
        anchorOrigin={{ vertical: "top", horizontal: "right" }}
        sx={{ mt: 7 }}
      >
        <Alert
          onClose={hideNotification}
          severity={notification.severity}
          variant="filled"
          sx={{
            width: "100%",
            "& .MuiAlert-message": { fontSize: "14px", fontWeight: 500 },
          }}
        >
          {notification.message}
        </Alert>
      </Snackbar>
    </>
  );
};